import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { fileUrl } from "@/lib/api";
import { Loader, EmptyState } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import Celebration from "@/components/Celebration";
import * as Icons from "lucide-react";
import { toast } from "sonner";

export default function Reviews() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["reviews"], queryFn: async () => (await api.get("/submissions?status=pending")).data });
  const [active, setActive] = useState(null);
  const [feedback, setFeedback] = useState("");
  const [rating, setRating] = useState(5);
  const [bonus, setBonus] = useState(0);
  const [celebrate, setCelebrate] = useState(null);

  if (isLoading) return <Loader />;

  const review = async (sub, action) => {
    const r = await api.post(`/submissions/${sub.id}/review`, { action, feedback, rating, bonus: Number(bonus) });
    if (action === "approve") {
      const badge = r.data.unlocked_badge;
      if (badge) setCelebrate({ title: badge.name, message: badge.description, icon: badge.icon, color: badge.color, rarity: badge.rarity, points: badge.points, heading: `${sub.student_name} unlocked a badge!`, subheading: "Badge Awarded" });
      toast.success(`Approved! ${sub.student_name} earned points 🎉`);
    } else { toast.info("Resubmission requested"); }
    setActive(null); setFeedback(""); setRating(5); setBonus(0);
    qc.invalidateQueries();
  };

  return (
    <div className="space-y-5" data-testid="reviews-page">
      <Celebration open={!!celebrate} onClose={() => setCelebrate(null)} data={celebrate} />
      <h2 className="font-head text-3xl text-csh-blue">Quest Reviews ✅</h2>
      {(!data || data.length === 0) ? <EmptyState icon="ClipboardCheck" title="All caught up!" text="No pending submissions to review." /> : (
        <div className="space-y-3">
          {data.map((sub) => (
            <div key={sub.id} className="rounded-3xl bg-white p-5 shadow-sm border border-black/5" data-testid={`review-${sub.id}`}>
              <div className="flex items-center gap-3">
                <img src={sub.student_avatar} alt="" className="h-11 w-11 rounded-xl object-cover" />
                <div className="flex-1">
                  <div className="font-head text-lg text-csh-blue">{sub.quest_name}</div>
                  <div className="text-sm text-muted-foreground">{sub.student_name} · {sub.submitted_at?.slice(0, 10)}</div>
                </div>
                <span className="rounded-full bg-yellow-100 px-3 py-1 text-xs font-bold text-yellow-700">Pending</span>
              </div>

              {sub.text && <p className="mt-3 rounded-xl bg-black/5 p-3 text-sm">{sub.text}</p>}
              {sub.link && <a href={sub.link} target="_blank" rel="noreferrer" className="mt-2 inline-flex items-center gap-1 text-sm font-bold text-csh-purple"><Icons.Link size={14} /> {sub.link}</a>}
              {sub.file_id && sub.file_url?.includes("file") && <video src={fileUrl(sub.file_id)} controls className="mt-3 w-full max-w-md rounded-xl" />}

              {active === sub.id ? (
                <div className="mt-3 space-y-3 rounded-2xl bg-csh-blue/5 p-4">
                  <Textarea value={feedback} onChange={(e) => setFeedback(e.target.value)} placeholder="Positive feedback e.g. 'Fantastic Anchor Line! Try slowing down your final sentence.'" className="rounded-xl border-2" data-testid="feedback-input" />
                  <div className="flex items-center gap-3">
                    <label className="text-sm font-bold">Rating:</label>
                    {[1, 2, 3, 4, 5].map((n) => <button key={n} onClick={() => setRating(n)}><Icons.Star size={20} className={n <= rating ? "fill-yellow-400 text-yellow-400" : "text-black/20"} /></button>)}
                    <label className="text-sm font-bold ml-2">Bonus:</label>
                    <input type="number" value={bonus} onChange={(e) => setBonus(e.target.value)} className="w-16 rounded-lg border-2 px-2 py-1 text-sm" />
                  </div>
                  <div className="flex gap-2">
                    <Button onClick={() => review(sub, "approve")} data-testid="approve-btn" className="flex-1 rounded-full bg-green-500 text-white font-bold hover:bg-green-600"><Icons.Check size={16} /> Approve & Award</Button>
                    <Button onClick={() => review(sub, "resubmit")} data-testid="resubmit-btn" variant="outline" className="flex-1 rounded-full font-bold"><Icons.RefreshCw size={16} /> Request Resubmission</Button>
                  </div>
                </div>
              ) : (
                <Button onClick={() => setActive(sub.id)} data-testid={`review-open-${sub.id}`} className="mt-3 rounded-full gradient-purple text-white font-bold">Review Submission</Button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
